import { Dialog, Transition } from "@headlessui/react";
import React, { Fragment, useState } from "react";
import { FiX } from "react-icons/fi";

import { GeedeeTooltipContent } from "./geedee/GeedeeTooltipContent";
import { hasPriceWithGeedeeDetails, hasPriceWithoutGeedeeDetails } from "./shared/formatters";
import { PriceWithGeedeeDetails, PriceWithoutGeedeeDetails, TooltipType } from "./shared/types";

interface AccountDetailsModalProps {
  type?: TooltipType;
  priceWithGeedeeDetails?: PriceWithGeedeeDetails | null;
  priceWithoutGeedeeDetails?: PriceWithoutGeedeeDetails | null;
}

export const AccountDetailsModal: React.FC<AccountDetailsModalProps> = ({
  type = "with_geedee",
  priceWithGeedeeDetails,
  priceWithoutGeedeeDetails,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const hasWithGeedeeDetails = hasPriceWithGeedeeDetails(priceWithGeedeeDetails);
  const hasWithoutGeedeeDetails = hasPriceWithoutGeedeeDetails(priceWithoutGeedeeDetails);

  if (type === "with_geedee" && !hasWithGeedeeDetails) return null;
  if (type === "without_geedee" && !hasWithoutGeedeeDetails) return null;

  const title =
    type === "with_geedee"
      ? "Detalhes do preço com energia solar"
      : "Detalhes do preço sem energia solar";

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="inline-flex size-4 cursor-pointer select-none items-center justify-center rounded-full border border-white/45 bg-white/15 text-[9px] font-bold leading-none text-white transition-colors hover:bg-white/25 focus:outline-none focus:ring-2 focus:ring-white/70"
        aria-label="Ver detalhes"
      >
        i
      </button>

      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[10000]" onClose={setIsOpen}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/60" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 translate-y-2 scale-95"
                enterTo="opacity-100 translate-y-0 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0 scale-100"
                leaveTo="opacity-0 translate-y-2 scale-95"
              >
                <Dialog.Panel className="w-full max-w-3xl overflow-hidden rounded-xl bg-white text-left shadow-xl transition-all">
                  <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4 md:px-6">
                    <Dialog.Title as="h2" className="text-lg font-semibold text-gray-900">
                      {title}
                    </Dialog.Title>

                    <button
                      type="button"
                      onClick={() => setIsOpen(false)}
                      className="inline-flex size-9 shrink-0 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary300"
                      aria-label="Fechar"
                    >
                      <FiX className="size-5" aria-hidden="true" />
                    </button>
                  </div>

                  <div className="max-h-[75vh] overflow-y-auto px-4 py-5 md:px-6">
                    <GeedeeTooltipContent
                      type={type}
                      priceWithGeedeeDetails={priceWithGeedeeDetails}
                      priceWithoutGeedeeDetails={priceWithoutGeedeeDetails}
                    />
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};
